import { useId } from "react";

import { Skeleton } from "../../core/Skeleton.js";
import { Toggle } from "../../core/Toggle.js";
import type { NotificationSettingsContent } from "./notificationTypes.js";

/**
 * One switch per category the store sends, in the store's words. A required category stays on
 * and says so; the others apply the moment they change.
 */
export function NotificationSettings({ intro, requiredLabel, state }: NotificationSettingsContent) {
  const idPrefix = useId();
  return (
    <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
      <div className="flex flex-col gap-4 px-4 pt-1 pb-4">
        <p className="font-body text-body-b2 text-text-secondary">{intro}</p>
        {state.status === "loading" ? (
          <div aria-busy="true" className="flex flex-col gap-4">
            <span className="sr-only">{state.label}</span>
            {[0, 1, 2, 3].map((row) => (
              <div key={row} className="flex items-center justify-between gap-3">
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-6 w-10 rounded-pill" />
              </div>
            ))}
          </div>
        ) : state.status === "error" ? (
          <p role="alert" className="font-body text-body-b2 text-text-secondary">
            {state.message}
          </p>
        ) : (
          <ul className="flex flex-col gap-4">
            {state.categories.map((setting) => (
              <li key={setting.id} className="flex items-center justify-between gap-3">
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="font-body text-body-b1-strong text-text-primary">
                    {setting.label}
                  </span>
                  {setting.required && (
                    <span
                      id={`${idPrefix}-${setting.id}`}
                      className="font-body text-body-b2 text-text-tertiary"
                    >
                      {requiredLabel}
                    </span>
                  )}
                </span>
                {/* Named by the same words that show beside it (Toggle's `label`). */}
                <Toggle
                  label={setting.label}
                  checked={setting.required || setting.enabled}
                  disabled={setting.required}
                  aria-describedby={setting.required ? `${idPrefix}-${setting.id}` : undefined}
                  onCheckedChange={(checked) => setting.onChange(checked)}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
